import { Component, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import {
    ConfigConstants,
    ConfigService,
    ModalClass,
    Notification,
    NotificationInterface,
    isDefined,
} from '@evorait/pace-core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { faCheck, faCloudUploadAlt, faEllipsisV, faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { BsModalService } from 'ngx-bootstrap/modal';
import { Observable, Subject, combineLatest } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, scan, startWith, take, takeUntil, tap } from 'rxjs/operators';
import { NotificationInstanceToken } from '../../core/interfaces/notification.interface';
import {
    clearDocBoxData,
    downloadDocuments,
    filterDocuments,
    selectAllDocuments,
    sortByField,
} from '../../store/actions/docBox.action';
import {
    enableDownloadSelector,
    getDocBoxFilesSelector,
    getDocBoxSelector,
    selectedDocumentsSelector,
} from '../../store/selectors/docBox.selector';
import { DocBoxState } from '../../store/state/docBox.state';
import { DOCUMENT_FILTERS, DocumentState } from '../../store/state/document.state';
import { FormsState } from '../../store/state/pace-forms.state';
import { UploadModalComponent } from './upload-modal/upload-modal.component';
import { mapDocBoxToNotificationData } from '../../utilities/docbox-utilities';

@Component({
    selector: 'pace-forms-docbox',
    templateUrl: './docbox.component.html',
    styleUrls: ['./docbox.component.scss'],
})
export class DocboxComponent implements OnInit, OnDestroy {
    @Input() public isSideMenuDocbox = false;

    public docBox$: Observable<DocBoxState>;
    public docBoxFiles$: Observable<DocumentState[]>;
    public disableDownloadButton$: Observable<boolean>;
    public toggleDocBoxFiles$: Subject<boolean> = new Subject<boolean>();

    public showMoreLessTranslation: string;
    public docBoxGroup: FormGroup;

    public faCloudUploadAlt = faCloudUploadAlt;
    public faCheck = faCheck;
    public filters = DOCUMENT_FILTERS;

    private notification: Notification | undefined;
    private destroy$ = new Subject<void>();

    public faSortDown = faSortDown;
    public faSortUp = faSortUp;
    public enableIndividualDownload: boolean;
    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly store: Store<FormsState>,
        private readonly formBuilder: FormBuilder,
        private readonly modalService: BsModalService,
        private readonly translate: TranslateService,
        @Inject(NotificationInstanceToken) private readonly notificationService: NotificationInterface,
        private readonly route: ActivatedRoute,
        private readonly configService: ConfigService,
    ) {
        this.enableIndividualDownload = this.configService.getEnableIndividualDocBoxDownload();
        this.docBoxGroup = formBuilder.group({
            checkbox: formBuilder.control({}),
            docTypeInputControl: formBuilder.control(''),
            titleInputControl: formBuilder.control(''),
            printNumberInputControl: formBuilder.control(''),
            dateInputControl: formBuilder.control(''),
            createdByInputControl: formBuilder.control(''),
            orderNumberInputControl: formBuilder.control(''),
            reportStatusInputControl: formBuilder.control(''),
            fileTypeInputControl: formBuilder.control(''),
        });
        this.docBoxFiles$ = combineLatest([
            this.store.select(getDocBoxFilesSelector),
            this.toggleDocBoxFiles$.pipe(
                scan((acc) => !acc, false),
                startWith(false),
                tap((value) => {
                    const translationToUse = value ? 'DOCBOX_SHOW_LESS' : 'DOCBOX_SHOW_MORE';
                    this.showMoreLessTranslation = <string>this.translate.instant(translationToUse);
                }),
            ),
        ]).pipe(
            map(([docBoxFiles, displayAllDocBoxFiles]) => {
                const twoYearsBefore = moment().set('year', moment().get('year') - 2);
                return docBoxFiles.filter((docBoxFile) => {
                    const momentDate =
                        docBoxFile.date && docBoxFile.date?.length > 0 ? moment(docBoxFile.date) : moment.invalid();
                    return displayAllDocBoxFiles || momentDate?.isAfter(twoYearsBefore);
                });
            }),
        );

        this.docBox$ = this.store.select(getDocBoxSelector);
        this.disableDownloadButton$ = this.store.select(enableDownloadSelector).pipe(map((enable) => !enable));
    }

    public ngOnDestroy(): void {
        // Free search DocBox and other DocBoxes share the same state,
        // so we clear it here to avoid any confusion or stale data.
        this.store.dispatch(clearDocBoxData());
        this.destroy$.next();
    }

    public ngOnInit(): void {
        if (!this.isSideMenuDocbox) {
            this.route.paramMap.pipe(take(1)).subscribe((paramMap) => {
                const frontId = paramMap.get('frontid');
                const maNum = paramMap.get('manum');
                const qmNum = paramMap.get('qmnum');
                if (!isDefined(frontId) || !isDefined(maNum) || !isDefined(qmNum)) {
                    this.log.warn(
                        `DocBoxComponent: ngOnInit: Notification id is incomplete '${frontId}:${maNum}:${qmNum}'`,
                    );
                    throw new Error('Missing Notification Identifier for DocBox list');
                }
                this.notification = this.notificationService.getNotificationStateByIdentifier({
                    frontId: frontId,
                    maNum: maNum,
                    qmNum: qmNum,
                });
            });
        }

        this.docBoxGroup.controls.docTypeInputControl.valueChanges
            .pipe(
                debounceTime(ConfigConstants.debounceTimeMs),
                distinctUntilChanged(),
                tap((newVal: string) => {
                    this.store.dispatch(filterDocuments({ property: DOCUMENT_FILTERS.docType, value: newVal }));
                }),
                takeUntil(this.destroy$),
            )
            .subscribe();

        this.docBoxGroup.controls.titleInputControl.valueChanges
            .pipe(
                debounceTime(ConfigConstants.debounceTimeMs),
                distinctUntilChanged(),
                tap((newVal: string) => {
                    this.store.dispatch(filterDocuments({ property: DOCUMENT_FILTERS.title, value: newVal }));
                }),
                takeUntil(this.destroy$),
            )
            .subscribe();

        this.docBoxGroup.controls.printNumberInputControl.valueChanges
            .pipe(
                debounceTime(ConfigConstants.debounceTimeMs),
                distinctUntilChanged(),
                tap((newVal: string) => {
                    this.store.dispatch(filterDocuments({ property: DOCUMENT_FILTERS.printNumber, value: newVal }));
                }),
                takeUntil(this.destroy$),
            )
            .subscribe();

        this.docBoxGroup.controls.dateInputControl.valueChanges
            .pipe(
                debounceTime(ConfigConstants.debounceTimeMs),
                distinctUntilChanged(),
                tap((newVal: string) => {
                    this.store.dispatch(filterDocuments({ property: DOCUMENT_FILTERS.date, value: newVal }));
                }),
                takeUntil(this.destroy$),
            )
            .subscribe();

        this.docBoxGroup.controls.createdByInputControl.valueChanges
            .pipe(
                debounceTime(ConfigConstants.debounceTimeMs),
                distinctUntilChanged(),
                tap((newVal: string) => {
                    this.store.dispatch(filterDocuments({ property: DOCUMENT_FILTERS.createdBy, value: newVal }));
                }),
                takeUntil(this.destroy$),
            )
            .subscribe();

        this.docBoxGroup.controls.orderNumberInputControl.valueChanges
            .pipe(
                debounceTime(ConfigConstants.debounceTimeMs),
                distinctUntilChanged(),
                tap((newVal: string) => {
                    this.store.dispatch(filterDocuments({ property: DOCUMENT_FILTERS.orderNumber, value: newVal }));
                }),
                takeUntil(this.destroy$),
            )
            .subscribe();

        this.docBoxGroup.controls.reportStatusInputControl.valueChanges
            .pipe(
                debounceTime(ConfigConstants.debounceTimeMs),
                distinctUntilChanged(),
                tap((newVal: string) => {
                    this.store.dispatch(filterDocuments({ property: DOCUMENT_FILTERS.reportStatus, value: newVal }));
                }),
                takeUntil(this.destroy$),
            )
            .subscribe();

        this.docBoxGroup.controls.fileTypeInputControl.valueChanges
            .pipe(
                debounceTime(ConfigConstants.debounceTimeMs),
                distinctUntilChanged(),
                tap((newVal: string) => {
                    this.store.dispatch(filterDocuments({ property: DOCUMENT_FILTERS.fileType, value: newVal }));
                }),
                takeUntil(this.destroy$),
            )
            .subscribe();
    }

    public selectAllDocuments(): void {
        this.store.dispatch(selectAllDocuments());
    }

    public clearFilter(filter: DOCUMENT_FILTERS): void {
        this.docBoxGroup.controls[`${filter}InputControl`].setValue('');
    }

    public downloadDocuments(): void {
        this.store
            .select(selectedDocumentsSelector)
            .pipe(take(1))
            .subscribe((docBoxIds) => this.store.dispatch(downloadDocuments({ docBoxIds: docBoxIds })));
    }

    public showUploadPopup(docboxEntry?: DocumentState): void {
        this.modalService.show(UploadModalComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_MEDIUM,
            initialState: {
                docBoxModel: docboxEntry,
                notification: this.getNotificationToUpload(docboxEntry),
            },
            ariaLabelledBy: 'upload-modal-title',
        });
    }

    public sortByField(fieldName: keyof DocumentState): void {
        this.store.dispatch(sortByField({ property: fieldName }));
    }

    public toggleDocBoxFiles(): void {
        this.toggleDocBoxFiles$.next(true);
    }

    private getNotificationToUpload(docboxEntry?: DocumentState): Notification {
        if (this.isSideMenuDocbox) {
            return mapDocBoxToNotificationData(this.configService.getUserFrontId(), docboxEntry);
        }

        if (!isDefined(this.notification)) {
            throw new Error('Notification is undefined for DocBox list');
        }
        return this.notification;
    }

    protected readonly faEllipsisV = faEllipsisV;
}
